import React, { CSSProperties, ReactElement, useState } from 'react';
import ReactDOM from 'react-dom';
import classNames from 'classnames';
import { usePopperTooltip } from 'react-popper-tooltip';
import ReactMarkdown from 'react-markdown';
import styles from './Tooltip.module.scss';

export type DirectionType =
    | 'auto'
    | 'auto-start'
    | 'auto-end'
    | 'top'
    | 'top-start'
    | 'top-end'
    | 'bottom'
    | 'bottom-start'
    | 'bottom-end'
    | 'right'
    | 'right-start'
    | 'right-end'
    | 'left'
    | 'left-start'
    | 'left-end';

interface Props {
    children: React.ReactChild;
    label: string | ReactElement;
    type?: 'default' | 'danger';
    direction?: DirectionType;
    align?: 'left' | 'center';
    interactive?: boolean;
    containerClass?: string;
    offset?: [number, number];
    onVisibleChange?: (state: boolean) => void;
    containerStyles?: CSSProperties;
    delayShow?: number;
    delayHide?: number;
    pointerCursor?: boolean;
    hideTooltip?: boolean;
    center?: boolean;
    noWidth?: boolean;
    /**
     * Also shows the tooltip while a focusable element inside `children` has
     * keyboard focus, and lets Escape hide it again — for a tooltip whose
     * content must not be hover-only. Off by default: hover behaviour is the
     * same either way.
     */
    openOnFocus?: boolean;
}

/**
 * Whether focus arrived from the keyboard. A mouse click focuses a button too
 * (in most browsers), and the hover tooltip already covers that case — keeping
 * it open until the next click elsewhere would only get in the way. Browsers
 * without `:focus-visible` treat every focus as a keyboard one.
 */
const isKeyboardFocus = (element: Element): boolean => {
    try {
        return element.matches(':focus-visible');
    } catch {
        return true;
    }
};

const Tooltip = ({
    children,
    label,
    type,
    direction = 'top',
    align = 'center',
    interactive = false,
    offset,
    onVisibleChange,
    containerStyles,
    containerClass,
    delayShow,
    delayHide,
    pointerCursor,
    hideTooltip,
    center,
    noWidth,
    openOnFocus = false,
}: Props): ReactElement => {
    const {
        getArrowProps,
        getTooltipProps,
        setTooltipRef,
        setTriggerRef,
        visible,
    } = usePopperTooltip({
        delayShow: delayShow !== undefined ? delayShow : 250,
        delayHide: delayHide !== undefined ? delayHide : 150,
        followCursor: false,
        placement: direction,
        interactive,
        offset,
        onVisibleChange,
    });

    // Keyboard visibility is kept beside the hook's own (hover) state rather
    // than handed to it as a `focus` trigger: the hook listens on the trigger
    // wrapper itself, and `focus` does not bubble up from the element inside.
    const [focusVisible, setFocusVisible] = useState<boolean>(false);

    const focusProps = openOnFocus
        ? {
              onFocus: (event: React.FocusEvent<HTMLDivElement>): void =>
                  setFocusVisible(isKeyboardFocus(event.target)),
              onBlur: (event: React.FocusEvent<HTMLDivElement>): void => {
                  if (
                      !event.currentTarget.contains(
                          event.relatedTarget as Node | null
                      )
                  ) {
                      setFocusVisible(false);
                  }
              },
              onKeyDown: (event: React.KeyboardEvent<HTMLDivElement>): void => {
                  if (event.key === 'Escape' && focusVisible) {
                      event.stopPropagation();
                      setFocusVisible(false);
                  }
              },
          }
        : {};

    return (
        <div
            className={classNames(styles.tooltip, containerClass, {
                [styles.cursor_help]: !pointerCursor && !!label,
                [styles.cursor_pointer]: pointerCursor,
                [styles.noWidth]: noWidth,
            })}
        >
            <div
                className={classNames(styles.children, {
                    [styles.center]: center,
                })}
                ref={setTriggerRef}
                {...focusProps}
            >
                {children}
            </div>
            {label &&
                !hideTooltip &&
                (visible || focusVisible) &&
                ReactDOM.createPortal(
                    <div
                        ref={setTooltipRef}
                        {...getTooltipProps({
                            className: classNames(styles['tooltip-container'], {
                                [styles.danger]: type === 'danger',
                                [styles.center]: align === 'center',
                            }),
                            style: containerStyles,
                        })}
                    >
                        <div
                            {...getArrowProps({
                                className: styles['tooltip-arrow'],
                            })}
                        />
                        {(typeof label === 'string' && (
                            <ReactMarkdown>{label}</ReactMarkdown>
                        )) || <div>{label}</div>}
                    </div>,
                    document.getElementById('tooltip-popper')
                )}
        </div>
    );
};

export default Tooltip;
